/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-12-20 08:34:34
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, change } from 'redux-form';
import { FormLayout, Stack, Button, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import RenderButtonStyles from 'src/components/views/quick-view-cart-settings/render-button-styles';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';
import RenderColorPickerPopover from 'src/components/views/common/render-color-picker-popover';
import { RenderTooltip } from 'src/components/views/common/render-tooltip';

import {
  BUTTON_STYLES,
  QUICKVIEW_ADD_TO_CART_STYLES,
  AJAX_CART_STYLES,
  SELECT_OPTION_IN_PRODUCT_ITEM_STYLES
} from 'src/constants/quick-view-cart-settings';
import { buildOptionList } from 'src/helpers';
import { stringifyRGBASpecialFormat } from 'src/helpers/color-helper';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';

type Props = WithI18nProps;
type State = {
  showQVColorSettings: boolean,
  showATCColorSettings: boolean,
  showATCAdvancedSettings: boolean,
  buttonStyles: Array<{ value: string, label: string }>,
  ajaxCartStyles: Array<{ value: string, label: string }>,
  selectOptionInProductItemStyles: Array<{ value: string, label: string }>
};
class ContentSettings extends Component<Props, State> {
  state = {
    showQVColorSettings: true,
    showATCColorSettings: true,
    showATCAdvancedSettings: true,
    buttonStyles: [],
    ajaxCartStyles: [],
    selectOptionInProductItemStyles: []
  };

  componentDidMount() {
    const { i18n } = this.props;

    const buttonStyles = buildOptionList(BUTTON_STYLES, i18n);
    const ajaxCartStyles = this.buildOptionListWithTooltip(AJAX_CART_STYLES, i18n);
    const selectOptionInProductItemStyles = this.buildOptionListWithTooltip(
      SELECT_OPTION_IN_PRODUCT_ITEM_STYLES,
      i18n
    );

    this.setState({
      buttonStyles,
      ajaxCartStyles,
      selectOptionInProductItemStyles
    });
  }

  render() {
    const {
      qvEnable,
      atcEnable,
      enableAjaxCart,
      atcBtnBackgroundColor,
      atcBtnHoverBackgroundColor,
      atcBtnBorderColor,
      atcBtnHoverBorderColor,
      atcBtnTextColor,
      atcBtnHoverTextColor,
      qvBtnBackgroundColor,
      qvBtnHoverBackgroundColor,
      qvBtnBorderColor,
      qvBtnHoverBorderColor,
      qvBtnTextColor,
      qvBtnHoverTextColor,
      updateField,
      i18n,
      language
    } = this.props;
    const {
      showQVColorSettings,
      showATCColorSettings,
      showATCAdvancedSettings,
      buttonStyles,
      ajaxCartStyles,
      selectOptionInProductItemStyles
    } = this.state;

    return (
      <FormLayout>
        <Field
          name="actionlist.alStyle"
          component={RenderButtonStyles}
          label={i18n.translate(
            'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.field1_Label'
          )}
          options={buttonStyles}
          onChange={(event, newValue) => this.changeButtonStyle(newValue)}
          language={language}
        />

        <div className="quick-view-group">
          <Field
            name="actionlist.qvEnable"
            component={RenderToggleSwitch}
            label={i18n.translate(
              'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.field2_Label'
            )}
          />
          {qvEnable && (
            <div className="groupFields">
              <div className="groupFields-label">
                <Button
                  disclosure={showQVColorSettings ? 'down' : 'up'}
                  plain
                  monochrome
                  removeUnderline
                  onClick={() => this.toggleState('showQVColorSettings')}>
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.gFQVColorSettings_Label'
                    )}
                  </TextStyle>
                </Button>
              </div>
              {showQVColorSettings && (
                <div className="groupFields-content">
                  <Stack distribution="equalSpacing">
                    <RenderColorPickerPopover
                      colorValue={qvBtnBackgroundColor}
                      fieldName="actionlist.qvBtnBackgroundColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover1_FieldLabel'
                      )}
                      updateField={updateField}
                    />

                    <RenderColorPickerPopover
                      colorValue={qvBtnHoverBackgroundColor}
                      fieldName="actionlist.qvBtnHoverBackgroundColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover2_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                  </Stack>
                  <Stack distribution="equalSpacing">
                    <RenderColorPickerPopover
                      colorValue={qvBtnTextColor}
                      fieldName="actionlist.qvBtnTextColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover3_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                    <RenderColorPickerPopover
                      colorValue={qvBtnHoverTextColor}
                      fieldName="actionlist.qvBtnHoverTextColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover4_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                  </Stack>
                  <Stack distribution="equalSpacing">
                    <RenderColorPickerPopover
                      colorValue={qvBtnBorderColor}
                      fieldName="actionlist.qvBtnBorderColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover5_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                    <RenderColorPickerPopover
                      colorValue={qvBtnHoverBorderColor}
                      fieldName="actionlist.qvBtnHoverBorderColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover6_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                  </Stack>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="add-to-card-group">
          <Field
            name="actionlist.atcEnable"
            component={RenderToggleSwitch}
            label={i18n.translate(
              'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.field3_Label'
            )}
            onChange={this.changeATCEnable}
          />
          {atcEnable && (
            <div className="groupFields">
              <div className="groupFields-label">
                <Button
                  disclosure={showATCColorSettings ? 'down' : 'up'}
                  plain
                  monochrome
                  removeUnderline
                  onClick={() => this.toggleState('showATCColorSettings')}>
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.gFATCColorSettings_Label'
                    )}
                  </TextStyle>
                </Button>
              </div>
              {showATCColorSettings && (
                <div className="groupFields-content">
                  <Stack distribution="equalSpacing">
                    <RenderColorPickerPopover
                      colorValue={atcBtnBackgroundColor}
                      fieldName="actionlist.atcBtnBackgroundColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover1_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                    <RenderColorPickerPopover
                      colorValue={atcBtnHoverBackgroundColor}
                      fieldName="actionlist.atcBtnHoverBackgroundColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover2_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                  </Stack>
                  <Stack distribution="equalSpacing">
                    <RenderColorPickerPopover
                      colorValue={atcBtnTextColor}
                      fieldName="actionlist.atcBtnTextColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover3_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                    <RenderColorPickerPopover
                      colorValue={atcBtnHoverTextColor}
                      fieldName="actionlist.atcBtnHoverTextColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover4_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                  </Stack>
                  <Stack distribution="equalSpacing">
                    <RenderColorPickerPopover
                      colorValue={atcBtnBorderColor}
                      fieldName="actionlist.atcBtnBorderColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover5_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                    <RenderColorPickerPopover
                      colorValue={atcBtnHoverBorderColor}
                      fieldName="actionlist.atcBtnHoverBorderColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.renderColorPickerPopover6_FieldLabel'
                      )}
                      updateField={updateField}
                    />
                  </Stack>
                </div>
              )}

              <div className="groupFields-label">
                <Button
                  disclosure={showATCAdvancedSettings ? 'down' : 'up'}
                  plain
                  monochrome
                  removeUnderline
                  onClick={() => this.toggleState('showATCAdvancedSettings')}>
                  <TextStyle variation="strong">
                    {i18n.translate(
                      'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.gFATCAdvancedSettings_Label'
                    )}
                  </TextStyle>
                </Button>
              </div>
              {showATCAdvancedSettings && (
                <div className="groupFields-content">
                  <Stack vertical>
                    <Field
                      name="general.enableAjaxCart"
                      component={RenderToggleSwitch}
                      label={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.field7_Label'
                      )}
                      tooltip={i18n.translate(
                        'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.field7_Tooltip'
                      )}
                    />
                    {enableAjaxCart && (
                      <div className="render-choice-flex-3 render-choice-with-tooltip">
                        <Field
                          name="general.selectOptionInProductItem"
                          component={RenderChoice}
                          label={i18n.translate(
                            'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.field8_Label'
                          )}
                          choices={selectOptionInProductItemStyles}
                        />
                      </div>
                    )}
                    {enableAjaxCart && (
                      <div className="render-choice-flex-3 render-choice-with-tooltip">
                        <Field
                          name="general.ajaxCartStyle"
                          component={RenderChoice}
                          label={i18n.translate(
                            'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.field9_Label'
                          )}
                          choices={ajaxCartStyles}
                        />
                      </div>
                    )}
                  </Stack>
                </div>
              )}
            </div>
          )}
        </div>
      </FormLayout>
    );
  }

  // Change quick view and add to card fields when change button style
  changeButtonStyle = newValue => {
    const value = newValue.value;
    const { defaultAlStyle, updateField } = this.props;
    const qv_atcStyle = QUICKVIEW_ADD_TO_CART_STYLES[value];
    let style = {};

    if (value === defaultAlStyle) {
      // reset data with style button from API
      const { initialValues } = this.props;
      const { actionlist } = initialValues;
      style = !_.isEmpty(actionlist) ? actionlist : qv_atcStyle;
    } else {
      // reset data with style button from local
      style = qv_atcStyle;
    }

    // Change fields
    [
      'qvEnable',
      'atcEnable',
      'atcBtnBackgroundColor',
      'atcBtnHoverBackgroundColor',
      'atcBtnBorderColor',
      'atcBtnHoverBorderColor',
      'atcBtnTextColor',
      'atcBtnHoverTextColor',
      'qvBtnBackgroundColor',
      'qvBtnHoverBackgroundColor',
      'qvBtnBorderColor',
      'qvBtnHoverBorderColor',
      'qvBtnTextColor',
      'qvBtnHoverTextColor'
    ].map(item => {
      return updateField(`actionlist[${item}]`, style[item]);
    });
  };

  // Toggle state field
  toggleState = (fieldName: string) => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Build option list with tooltip
  buildOptionListWithTooltip = (
    constantOptions: Array<{ value: string, label: string }>,
    i18n: Object
  ) => {
    if (_.isEmpty(constantOptions)) {
      return [];
    }

    const result = _.map(constantOptions, function (option) {
      return {
        value: option.value,
        label: (
          <React.Fragment>
            {i18n.translate(option.label)}
            {option.tooltipImage && (
              <RenderTooltip
                content={<img src={option.tooltipImage} alt={i18n.translate(option.label)} />}
                icon={true}
              />
            )}
            {option.tooltipText && (
              <RenderTooltip content={i18n.translate(option.tooltipText)} icon={true} />
            )}
          </React.Fragment>
        )
      };
    });

    return result;
  };

  changeATCEnable = (e, value) => {
    if (!value) {
      const { updateField } = this.props;
      updateField('general.enableAjaxCart', false);
    }
  };
}

/**
 * Get color value from default layout settings form
 *
 * @param  {number} name name of the field to get from form
 * @param  {number} list list fields of form
 *
 * @return {string}
 */
const getColor = (name, list) => {
  let result = _.get(list, name, {
    rgb: {
      r: 255,
      g: 255,
      b: 255,
      a: 1
    }
  });

  // Convert hsv to rgba color
  if (_.has(result, 'rgb')) {
    result = stringifyRGBASpecialFormat(result.rgb);
  }
  return result;
};

const mapStateToProps = (state, props) => {
  const formQuickViewCartSettings = getFormValues('QuickViewCartSettingsForm')(state);
  const qvEnable = _.get(formQuickViewCartSettings, 'actionlist.qvEnable', false);
  const atcEnable = _.get(formQuickViewCartSettings, 'actionlist.atcEnable', false);
  const enableAjaxCart = _.get(formQuickViewCartSettings, 'general.enableAjaxCart', true);

  let colorList = {};
  [
    'atcBtnBackgroundColor',
    'atcBtnHoverBackgroundColor',
    'atcBtnBorderColor',
    'atcBtnHoverBorderColor',
    'atcBtnTextColor',
    'atcBtnHoverTextColor',
    'qvBtnBackgroundColor',
    'qvBtnHoverBackgroundColor',
    'qvBtnBorderColor',
    'qvBtnHoverBorderColor',
    'qvBtnTextColor',
    'qvBtnHoverTextColor'
  ].map(item => {
    colorList[item] = getColor(`actionlist.${item}`, formQuickViewCartSettings);
    return '';
  });

  return {
    ...colorList,
    qvEnable,
    atcEnable,
    enableAjaxCart,
    defaultAlStyle: _.get(
      state.filterSettings,
      'filterSettings.actionlist.alStyle',
      'bc-al-style4'
    ),
    language: _.get(state.oauth, 'tenantApp.appLanguage', APP_LANGUAGE_ENGLISH_VALUE)
  };
};

const mapDispatchToProps = dispatch => ({
  updateField: (id, data) => dispatch(change(`QuickViewCartSettingsForm`, id, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(ContentSettings));
